import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, errorMessage } from "../../api";
import type { AuditEntry } from "../../api/types";
import { ErrorBox, Spinner } from "../../components/bits";
import { formatDateTime } from "../../lib/format";
import { Forbidden, useOrgData } from "./shared";

const PAGE = 100;

export function AuditTab() {
  const location = useLocation();
  const first = useOrgData((id) => api.admin.audit(id, { limit: PAGE }));
  const [more, setMore] = useState<AuditEntry[]>([]);
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [actor, setActor] = useState("");
  const [kind, setKind] = useState("");

  const entries = useMemo(() => [...(first.data?.entries ?? []), ...more], [first.data, more]);
  const actors = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of entries) if (e.actor) m.set(actorKey(e), e.actor.display_name);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [entries]);
  const kinds = useMemo(() => [...new Set(entries.map((e) => e.kind))].sort(), [entries]);
  const shown = entries.filter((e) => (!actor || actorKey(e) === actor) && (!kind || e.kind === kind));
  const done = exhausted || (first.data !== null && first.data.entries.length < PAGE);

  if (first.forbidden) return <Forbidden />;

  return (
    <section aria-labelledby="audit-title" className="admin-section">
      <div className="section-head">
        <h2 id="audit-title">Audit log</h2>
        <div className="section-actions">
          <label className="inline-field small">
            <span>Person</span>
            <select value={actor} onChange={(e) => setActor(e.target.value)}>
              <option value="">Everyone</option>
              {actors.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-field small">
            <span>Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">All</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <p className="muted small">Filters apply to the entries loaded so far. Newest first.</p>
      {first.error && <ErrorBox error={first.error} onRetry={first.reload} />}
      {!first.data && !first.error ? (
        <Spinner />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                  <th>Item</th>
                  <th>Field</th>
                  <th>Origin</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => (
                  <tr key={e.id}>
                    <td className="small nowrap" title={e.created_at}>
                      {formatDateTime(e.created_at)}
                    </td>
                    <td>
                      {e.actor?.id ? (
                        e.actor.display_name
                      ) : (
                        <span className="muted">{e.actor?.display_name ?? "System"}</span>
                      )}
                    </td>
                    <td>
                      <span className="chip">{e.kind}</span>
                    </td>
                    <td>
                      {e.item_id ? (
                        <Link to={`/items/${e.item_id}`} state={{ background: location }}>
                          {e.item_title ?? "(deleted item)"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="small mono">{e.path || "—"}</td>
                    <td className="small muted origin-cell" title={e.origin ? JSON.stringify(e.origin, null, 2) : ""}>
                      {e.origin ? originText(e.origin) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 0 && <p className="muted small pad">No entries match.</p>}
          {moreError && <ErrorBox error={moreError} />}
          <div className="load-more">
            {done ? (
              <span className="muted small">End of log.</span>
            ) : (
              <button
                type="button"
                className="btn"
                disabled={loading}
                onClick={async () => {
                  if (!first.orgId) return;
                  const last = entries[entries.length - 1];
                  if (!last) return;
                  setLoading(true);
                  setMoreError(null);
                  try {
                    const r = await api.admin.audit(first.orgId, { limit: PAGE, before: last.id });
                    setMore((m) => [...m, ...r.entries]);
                    if (r.entries.length < PAGE) setExhausted(true);
                  } catch (err) {
                    setMoreError(errorMessage(err));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/** System/sync entries have no actor id: group them by display name. */
function actorKey(e: AuditEntry): string {
  if (!e.actor) return "system";
  return e.actor.id ?? `name:${e.actor.display_name}`;
}

function originText(origin: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const k of ["source", "connector_type", "client", "via"]) {
    const v = origin[k];
    if (typeof v === "string") parts.push(v);
  }
  return parts.length ? parts.join(" · ") : Object.keys(origin).join(", ");
}
